import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux"; 
import "./addUsername.css";
import { phoneNumberVerification, phoneVerificationRequest, refreshUserDetails } from "../../actions/user";
import { toast } from "react-toastify";
import PhoneInput from 'react-phone-input-2';

const OTP_LENGTH = 6;

const normalizePhoneInput = (value = "") => String(value).replace(/\D/g, "");

const formatPhoneForApi = (value = "") => normalizePhoneInput(value);

const maskPhoneNumber = (value = "") => {
  const digits = normalizePhoneInput(value);
  return digits ? `*****${digits.slice(-4)}` : "";
};

const isAlreadyVerifiedMessage = (message = "") => message.toLowerCase().includes("already verified");

const VerifyNumber = () => {
  const userDetails = useSelector((state) => state.user.user);
  const allowedCountries = useSelector((state) => state.country.allowedCountries);
  const [otp, setOtp] = useState(new Array(OTP_LENGTH).fill(""));
  const inputRefs = useRef([]);
  const [showOtpInput, setShowOtpInput] = useState(false);
  const [pinId, setPinId] = useState("");
  const hasAutoSubmittedOtp = useRef(false);

  const dispatch = useDispatch();
  const [phone, setPhone] = useState(normalizePhoneInput(userDetails?.phone || ""));
  const [userPhoneNumber, setUserPhoneNumber] = useState(formatPhoneForApi(userDetails?.phone || ""));
  const [isEdited, setIsEdited] = useState(userPhoneNumber ? true : false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const closeButtonRef = useRef(null);
  const [countdown, setCountdown] = useState(60);

  const formatCountdown = (seconds) => {
    const minutes = Math.floor(seconds / 60).toString().padStart(2, "0");
    const secs = (seconds % 60).toString().padStart(2, "0");
    return `${minutes}:${secs}`;
  };

  const completeVerifiedFlow = useCallback(async () => {
    setErrorMessage("");
    setSuccessMessage("Phone number already verified");
    setShowOtpInput(false);
    setCountdown(60);
    setOtp(new Array(OTP_LENGTH).fill(""));
    hasAutoSubmittedOtp.current = false;

    await refreshUserDetails(dispatch);

    setTimeout(() => {
      closeButtonRef.current?.click();
    }, 1200);
  }, [dispatch]);

  useEffect(() => {
    if (!showOtpInput || countdown <= 0) return;

    const timer = setInterval(() => {
      setCountdown((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [showOtpInput, countdown]);

  const handleInput = (e, index) => {
      const value = e.target.value;
      if (isNaN(value)) return;

      const newOtp = [...otp];
      // Only take the last character if multiple characters are pasted
      newOtp[index] = value.slice(-1);
      setOtp(newOtp);
      hasAutoSubmittedOtp.current = false;

      // Move to next input if value is entered
      if (value && index < OTP_LENGTH - 1) {
          inputRefs.current[index + 1].focus();
      }
  };

  const handleKeyDown = (e, index) => {
      // Handle backspace
      if (e.key === "Backspace") {
          if (!otp[index] && index > 0) {
              // If current input is empty, move to previous input
              const newOtp = [...otp];
              newOtp[index - 1] = "";
              setOtp(newOtp);
              inputRefs.current[index - 1].focus();
          }
      }
  };

  useEffect(() => {
      const normalizedPhone = normalizePhoneInput(userDetails?.phone || "");
      setPhone(normalizedPhone);
      setUserPhoneNumber(formatPhoneForApi(normalizedPhone));
  }, [userDetails]);

  // Handle Input Change
  const handleChange = (num) => {
    setIsEdited(true);
    setErrorMessage(""); 
    setSuccessMessage("");
    const normalizedPhone = normalizePhoneInput(num);
    setPhone(normalizedPhone);
    setUserPhoneNumber(formatPhoneForApi(normalizedPhone));
  };

  // Handle Form Submission
  const handleSubmitNumber = async (event) => {
    event?.preventDefault?.();

    if (userDetails?.phone_verified_at) {
      await completeVerifiedFlow();
      return;
    }

    if (!userPhoneNumber) {
      setErrorMessage("Please enter a valid phone number.");
      return;
    }
    
    const credentials = {
      phone_number: userPhoneNumber,
    };
    
    setLoading(true);
    try {
      const response = await phoneVerificationRequest(credentials);
      // console.log(response);

      setPinId(response.data.pinId);
      const responsePhone = normalizePhoneInput(response.data.phone_number || userPhoneNumber);
      setPhone(responsePhone);
      setUserPhoneNumber(formatPhoneForApi(responsePhone));
      setSuccessMessage('Verification Code Has Been Sent')
      setTimeout(() => setSuccessMessage(""), 5000);
      setShowOtpInput(true);
      setCountdown(60);            
      setOtp(new Array(OTP_LENGTH).fill(""));
      hasAutoSubmittedOtp.current = false;

    } catch (error) {
      
      const responseErrors = error?.response?.data?.errors;
      const fallbackMessage = error?.response?.data?.message || "Verification request failed, Please try again";

      if (isAlreadyVerifiedMessage(fallbackMessage)) {
        await completeVerifiedFlow();
        return;
      }
      
      if (responseErrors && Object.keys(responseErrors).length > 0) {
        const errorMessages = Object.values(responseErrors)
          .flat()
          .filter(Boolean);

        setErrorMessage(errorMessages.length > 1 
          ? errorMessages[0] 
          : errorMessages.join("<br/>")
        );
      } else {
        setErrorMessage(fallbackMessage);
      }

      // console.error(error);
      setTimeout(() => setErrorMessage(""), 7000);
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = useCallback(async (otpValue) => {
    if (loading) return;

    if (otpValue.length !== OTP_LENGTH) {
      toast.error("Invalid OTP value");
      return;
    }

    const credentials = {
      phone_number: userPhoneNumber,
      pin: otpValue,
      pinId: pinId,
    };
    
    setLoading(true);
    try {
      await phoneNumberVerification( credentials);

      setSuccessMessage('Phone Verification Successful');
      setTimeout(() => setSuccessMessage(""), 5000);

      setUserPhoneNumber("");
      setPhone("");
      setOtp(new Array(OTP_LENGTH).fill(""));
      hasAutoSubmittedOtp.current = false;
      setIsEdited(false);
      setShowOtpInput(false);

      await refreshUserDetails(dispatch);

      setTimeout(() => {
        closeButtonRef.current?.click();
      }, 3000);
    } catch (error) {
      const responseErrors = error?.response?.data?.errors;
      const fallbackMessage = error?.response?.data?.message || "Verification failed, Please try again";

      if (isAlreadyVerifiedMessage(fallbackMessage)) {
        await completeVerifiedFlow();
        return;
      }
      
      if (responseErrors && Object.keys(responseErrors).length > 0) {
        const errorMessages = Object.values(responseErrors)
          .flat()
          .filter(Boolean); 

        setErrorMessage(errorMessages.length > 1 
          ? errorMessages[0] 
          : errorMessages.join("<br/>")
        );
      } else {
        setErrorMessage(fallbackMessage);
      }

      // console.error(error);
      setTimeout(() => setErrorMessage(""), 7000);
    } finally {
      setLoading(false);
    }
  }, [completeVerifiedFlow, dispatch, loading, pinId, userPhoneNumber]);

  const handleOtpSubmit = async (event) => {
    event.preventDefault();
    verifyOtp(otp.join(""));
  };

  useEffect(() => {
    const otpValue = otp.join("");

    if (!showOtpInput || loading || hasAutoSubmittedOtp.current || otpValue.length !== OTP_LENGTH) return;

    hasAutoSubmittedOtp.current = true;
    verifyOtp(otpValue);
  }, [loading, otp, showOtpInput, verifyOtp]);



  return (
    <section className="modal fade user-actions-modal" id="verifyNumberModal" tabIndex="-1" aria-labelledby="verifyNumberModalLabel" aria-hidden="true">
      <div className="modal-dialog">
        <div className="modal-content pb-5">
          <div className="d-flex justify-content-end p-3">
            <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close" ref={closeButtonRef}></button>
          </div>
          <div className="modal-body px-4 px-md-5">
            <h4 className="mb-1 text-center">Phone Number Verification</h4>

            {errorMessage && <p className="text-center error-container">{errorMessage}</p>}
            {successMessage && <p className="text-center success-container">{successMessage}</p>}

            <form onSubmit={showOtpInput ? handleOtpSubmit : handleSubmitNumber}>
              {!showOtpInput ? (
                <div className="form-group">
                  <label>{userPhoneNumber ? "Confirm your" : ""} Phone Number</label>
                  <PhoneInput
                    country={userDetails?.country_code?.toLowerCase() || 'ng'}
                    value={phone}
                    onChange={handleChange}
                    onlyCountries={allowedCountries.map((country) => country.toLowerCase())}
                  />
                </div>
              ) : (
                <div className="form-group mb-3 text-center">
                  <p className="mb-2 text-muted">
                    Enter the OTP code that has been sent to your mobile number <strong>{maskPhoneNumber(phone)}</strong>
                  </p>
                  <button 
                    type="button" 
                    className="btn btn-link btn-sm p-0 text-decoration-none" 
                    onClick={() => {
                      setShowOtpInput(false);
                      setCountdown(60);
                      setOtp(new Array(OTP_LENGTH).fill(""));
                      setErrorMessage("");
                      setSuccessMessage("");
                    }}
                  >
                    Change phone number
                  </button>
                </div>
              )}

              {showOtpInput && (
                <div className='form__group'>
                    <div className="otp-input-container mx-2">
                        {otp.map((digit, index) => (
                            <input
                                key={index}
                                type="text"
                                maxLength={1}
                                value={digit}
                                className='auth__form__input verify-code'
                                onChange={(e) => handleInput(e, index)}
                                onKeyDown={(e) => handleKeyDown(e, index)}
                                ref={(el) => inputRefs.current[index] = el}
                                inputMode="numeric"
                                autoComplete="one-time-code"
                            />
                        ))}
                    </div>
                </div>
              )}


              <button className="confirm-usernameBtn mt-4 mb-2" type="submit" disabled={!isEdited || loading}>
                {loading ? <span className="spinner-border spinner-border-sm"></span> : showOtpInput ? "Verify Code" : "Submit"}
              </button>

              {showOtpInput && (
                <>
                  {countdown > 0 ? (
                    <div className="text-center small text-muted"><p>Resend OTP in <span className="text-danger">{formatCountdown(countdown)}</span></p></div>
                      ) : (
                    <div className="d-flex justify-content-center align-items-center">
                      <button type="button" className="btn btn-link btn-sm p-0 fw-bold text-decoration-none" onClick={handleSubmitNumber} disabled={loading}>
                        Resend OTP
                      </button>
                    </div>
                  )}
                </>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default VerifyNumber;
